import pako from 'pako';

export interface ParsedItem {
  rarity: 'NORMAL' | 'MAGIC' | 'RARE' | 'UNIQUE' | 'RELIC';
  name: string;
  base: string;
  implicits: string[];
  explicits: string[];
  defense: Partial<Record<'ar' | 'ev' | 'es' | 'ward', number>>;
  corrupted: boolean;
}

export interface ParsedGem {
  name: string;
  level: number;
  quality: number;
  variantId: string;
  skillId: string;
  isSupport: boolean;
  isAwakened: boolean;
}

export interface GemGroup {
  slot: string;          // e.g. "Helmet", "Body Armour", "Weapon 1", "Gloves"
  label: string;         // optional label from PoB; falls back to slot
  gems: ParsedGem[];
}

export interface BuildStats {
  life?: number; lifePercent?: number;
  es?: number; ward?: number; mana?: number; manaUnreserved?: number;
  ehp?: number;
  fireRes?: number; coldRes?: number; lightningRes?: number; chaosRes?: number;
  evadeChance?: number; blockChance?: number; pdr?: number;
  totalDPS?: number; speed?: number;
  hitChance?: number; critChance?: number; critMulti?: number;
  bandit?: string;
  pantheonMajor?: string; pantheonMinor?: string;
  configSummary?: string;
}

export interface ParsedPoB {
  className: string;
  ascendClassName: string;
  level: string;
  itemSets: { id: string; title: string; active: boolean }[];
  equipped: { slot: string; item: ParsedItem }[];
  jewels: ParsedItem[];
  gemGroups: GemGroup[];
  stats: BuildStats;
}

const FLASK_BASES = [
  'Divine Life Flask', 'Eternal Life Flask', 'Sacred Life Flask', 'Divine Mana Flask',
  'Eternal Mana Flask', 'Divine Hybrid Flask', 'Quicksilver Flask', 'Granite Flask',
  'Jade Flask', 'Basalt Flask', 'Quartz Flask', 'Silver Flask', 'Diamond Flask',
  'Sulphur Flask', 'Stibnite Flask', 'Bismuth Flask', 'Topaz Flask', 'Ruby Flask',
  'Sapphire Flask', 'Amethyst Flask', 'Gold Flask', 'Aquamarine Flask', 'Iron Flask',
];

const SLOT_ORDER = [
  'Weapon 1', 'Weapon 2', 'Weapon 1 Swap', 'Weapon 2 Swap',
  'Helmet', 'Body Armour', 'Gloves', 'Boots',
  'Belt', 'Amulet', 'Ring 1', 'Ring 2',
  'Flask 1', 'Flask 2', 'Flask 3', 'Flask 4', 'Flask 5',
];

/** Strip PoB inline color codes (`^xRRGGBB`, `^N`) and trim whitespace. */
function stripPoBColor(s: string): string {
  return s.replace(/\^x[0-9A-Fa-f]{6}/g, '').replace(/\^[0-9]/g, '').trim();
}

/** Decode a PoB code (base64url + zlib) into XML string. */
export function decodePoBCode(code: string): string {
  const cleaned = code.trim().replace(/^"+|"+$/g, '').replace(/-/g, '+').replace(/_/g, '/');
  const padded = cleaned + '='.repeat((4 - cleaned.length % 4) % 4);
  const binary = atob(padded);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
  const decompressed = pako.inflate(bytes);
  return new TextDecoder().decode(decompressed);
}

function extractDefense(text: string): ParsedItem['defense'] {
  const defense: ParsedItem['defense'] = {};
  const patterns: Array<[keyof ParsedItem['defense'], RegExp]> = [
    ['ar', /^Armour:\s*(\d+)$/],
    ['ev', /^Evasion:\s*(\d+)$/],
    ['es', /^Energy Shield:\s*(\d+)$/],
    ['ward', /^Ward:\s*(\d+)$/],
  ];
  for (const line of text.split('\n')) {
    const t = line.trim();
    for (const [key, pat] of patterns) {
      const m = t.match(pat);
      if (m) defense[key] = parseInt(m[1], 10);
    }
  }
  return defense;
}

/** Parse a single <Item> text block into structured data. */
export function parseItem(text: string): ParsedItem {
  const lines = text.split('\n').map(l => l.trim()).filter(l => l.length > 0);
  let rarity: ParsedItem['rarity'] = 'NORMAL';
  let name = '';
  let base = '';
  const implicits: string[] = [];
  const explicits: string[] = [];
  let implicitCount = 0;
  let seenImplicits = false;
  let collected = 0;
  let corrupted = false;

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    if (line.startsWith('Rarity:')) {
      const r = line.split(':', 2)[1].trim().toUpperCase() as ParsedItem['rarity'];
      rarity = r;
      if (r === 'UNIQUE' || r === 'RARE' || r === 'RELIC') {
        name = lines[i + 1] ?? '';
        base = lines[i + 2] ?? '';
      } else if (r === 'MAGIC') {
        name = lines[i + 1] ?? '';
        const cleaned = name.replace(/\s+of\s+\w+(\s+\w+)?$/, '');
        base = '';
        for (const b of FLASK_BASES) { if (cleaned.includes(b)) { base = b; break; } }
        if (!base) {
          const parts = cleaned.split(/\s+/);
          base = parts.length >= 3 ? parts.slice(1).join(' ') : cleaned;
        }
      }
    }
  }

  for (const line of lines) {
    if (line === 'Corrupted') corrupted = true;
    if (line.startsWith('Implicits:')) {
      const n = parseInt(line.split(':')[1]?.trim() || '0', 10);
      implicitCount = isNaN(n) ? 0 : n;
      seenImplicits = true;
      continue;
    }
    if (seenImplicits) {
      if (collected < implicitCount) {
        implicits.push(line); collected++;
      } else if (
        line.startsWith('Note:') || line === 'Split' ||
        line === 'Fractured Item' || line === 'Corrupted' ||
        line.startsWith('Foil Unique')
      ) {
        continue;
      } else {
        explicits.push(line);
      }
    } else {
      // Metadata before Implicits header that we don't want as mods
      // (already handled via `seenImplicits` flag)
    }
  }

  // Strip metadata-like lines that are never trade-filterable stats
  const notMod = (l: string) =>
    /^(Limited to|Requires|Item Level|LevelReq|Unique ID|Radius|Item\s*class|Allocates\b).*$/.test(l)
      && !l.startsWith('Allocates '); // keep "Allocates X" mods
  for (const arr of [implicits, explicits]) {
    for (let i = arr.length - 1; i >= 0; i--) {
      const l = arr[i].replace(/\{[^}]*\}/g, '').trim();
      if (/^(Limited to|Requires |Item Level|LevelReq|Unique ID|Radius):?/i.test(l)) {
        arr.splice(i, 1);
      } else if (notMod(l)) {
        arr.splice(i, 1);
      }
    }
  }

  return { rarity, name, base, implicits, explicits, defense: extractDefense(text), corrupted };
}

export class UnsupportedPoBError extends Error {
  constructor(public reason: string, message: string) {
    super(message);
    this.name = 'UnsupportedPoBError';
  }
}

/** Top-level: decode PoB, return structured build + equipped items + jewels.
 *  If `itemSetId` is given, use that set instead of the active one.
 *  Throws UnsupportedPoBError for PoE2 builds or malformed data. */
export function parsePoB(code: string, itemSetId?: string): ParsedPoB {
  const xmlText = decodePoBCode(code);
  const doc = new DOMParser().parseFromString(xmlText, 'text/xml');

  // Sanity check — must be a Path of Building root element
  const root = doc.documentElement;
  if (!root || (root.tagName !== 'PathOfBuilding' && root.tagName !== 'PathOfBuilding2')) {
    throw new UnsupportedPoBError(
      'invalid',
      `Not a valid Path of Building export (root is <${root?.tagName ?? '?'}>).`
    );
  }

  const buildEl = doc.querySelector('Build');

  // Reject PoE2 builds — trade URLs differ, items differ, this tool is PoE1 only
  const targetVersion = buildEl?.getAttribute('targetVersion') ?? '';
  const isPoE2 = root.tagName === 'PathOfBuilding2' ||
                 targetVersion.startsWith('poe2') ||
                 targetVersion.startsWith('2_') ||
                 targetVersion === '4_0';
  if (isPoE2) {
    throw new UnsupportedPoBError(
      'poe2',
      `This is a Path of Exile 2 build (targetVersion="${targetVersion}"). Only PoE1 is supported right now — PoE2 has a different trade system.`
    );
  }
  if (targetVersion && targetVersion !== '3_0') {
    throw new UnsupportedPoBError(
      'unknown_version',
      `Unsupported PoB targetVersion="${targetVersion}". Only PoE1 (3_0) is supported.`
    );
  }
  const className = buildEl?.getAttribute('className') ?? '';
  const ascendClassName = buildEl?.getAttribute('ascendClassName') ?? '';
  const level = buildEl?.getAttribute('level') ?? '';

  // Item ID → text
  const items: Record<string, string> = {};
  for (const item of Array.from(doc.querySelectorAll('Item'))) {
    const id = item.getAttribute('id');
    const text = (item.textContent ?? '').trim();
    if (id) items[id] = text;
  }

  const itemsElem = doc.querySelector('Items');
  const activeSetId = itemsElem?.getAttribute('activeItemSet') ?? '1';
  const targetSetId = itemSetId ?? activeSetId;

  const itemSets = Array.from(itemsElem?.querySelectorAll('ItemSet') ?? []).map(s => ({
    id: s.getAttribute('id') ?? '',
    title: stripPoBColor(s.getAttribute('title') ?? ''),
    active: s.getAttribute('id') === activeSetId,
  }));

  const activeSet = Array.from(itemsElem?.querySelectorAll('ItemSet') ?? [])
    .find(s => s.getAttribute('id') === targetSetId);

  const equipped: ParsedPoB['equipped'] = [];
  for (const slotName of SLOT_ORDER) {
    const slot = Array.from(activeSet?.querySelectorAll('Slot') ?? [])
      .find(s => s.getAttribute('name') === slotName);
    const iid = slot?.getAttribute('itemId');
    if (iid && iid !== '0' && items[iid]) {
      equipped.push({ slot: slotName, item: parseItem(items[iid]) });
    }
  }

  // Jewels from Tree>Spec>Sockets — pick the spec matching the chosen item set
  // by title (color codes stripped); fall back to <Tree activeSpec="N"> (1-based);
  // last resort: first spec.
  const jewels: ParsedItem[] = [];
  const treeEl = doc.querySelector('Tree');
  const specs = Array.from(treeEl?.querySelectorAll('Spec') ?? []);
  const targetTitle = stripPoBColor(activeSet?.getAttribute('title') ?? '');
  // Multiple specs can share a title (e.g. a duplicated empty tree) — prefer
  // the matching spec that actually has socketed jewels, else the first match.
  const titleMatches = targetTitle === '' ? []
    : specs.filter(s => stripPoBColor(s.getAttribute('title') ?? '') === targetTitle);
  let spec: Element | undefined =
    titleMatches.find(s => s.querySelectorAll('Socket').length > 0) ?? titleMatches[0];
  if (!spec) {
    const activeSpecIdx = parseInt(treeEl?.getAttribute('activeSpec') ?? '1', 10);
    spec = specs[(isNaN(activeSpecIdx) ? 1 : activeSpecIdx) - 1] ?? specs[0];
  }
  for (const sock of Array.from(spec?.querySelectorAll('Socket') ?? [])) {
    const iid = sock.getAttribute('itemId');
    if (iid && iid !== '0' && items[iid]) {
      jewels.push(parseItem(items[iid]));
    }
  }

  // Gems from Skills > SkillSet > Skill > Gem — keep grouped by socket slot
  const gemGroups: GemGroup[] = [];
  const skillsRoot = doc.querySelector('Skills SkillSet') ?? doc.querySelector('Skills');
  for (const skillEl of Array.from(skillsRoot?.querySelectorAll('Skill') ?? [])) {
    const slot = skillEl.getAttribute('slot') ?? 'Other';
    const label = skillEl.getAttribute('label') ?? '';
    const enabled = skillEl.getAttribute('enabled') !== 'false';
    if (!enabled) continue;
    const gemList: ParsedGem[] = [];
    for (const gemEl of Array.from(skillEl.querySelectorAll('Gem'))) {
      const name = (gemEl.getAttribute('nameSpec') ?? '').trim();
      if (!name) continue;
      const level = parseInt(gemEl.getAttribute('level') ?? '1', 10);
      const quality = parseInt(gemEl.getAttribute('quality') ?? '0', 10);
      const variantId = gemEl.getAttribute('variantId') ?? '';
      const skillId = gemEl.getAttribute('skillId') ?? '';
      const gemEnabled = gemEl.getAttribute('enabled') !== 'false';
      if (!gemEnabled) continue;
      const isSupport = skillId.startsWith('Support') || variantId.startsWith('Support');
      const isAwakened = skillId.startsWith('Awakened') || variantId.startsWith('Awakened') || name.startsWith('Awakened ');
      gemList.push({ name, level, quality, variantId, skillId, isSupport, isAwakened });
    }
    if (gemList.length) {
      gemGroups.push({ slot, label, gems: gemList });
    }
  }

  // Build computed stats from <Build><PlayerStat stat="X" value="Y"/>
  const statMap: Record<string, number> = {};
  if (buildEl) {
    for (const ps of Array.from(buildEl.querySelectorAll('PlayerStat'))) {
      const stat = ps.getAttribute('stat');
      const value = parseFloat(ps.getAttribute('value') ?? '0');
      if (stat && !isNaN(value)) statMap[stat] = value;
    }
  }
  const stats: BuildStats = {
    life: statMap.LifeUnreserved ?? statMap.Life,
    lifePercent: statMap.LifeUnreservedPercent,
    es: statMap.EnergyShield,
    ward: statMap.Ward,
    mana: statMap.ManaUnreserved ?? statMap.Mana,
    ehp: statMap.TotalEHP,
    fireRes: statMap.FireResist,
    coldRes: statMap.ColdResist,
    lightningRes: statMap.LightningResist,
    chaosRes: statMap.ChaosResist,
    evadeChance: statMap.MeleeEvadeChance,
    blockChance: statMap.BlockChance,
    pdr: statMap.PhysicalDamageReduction,
    totalDPS: statMap.TotalDPS,
    speed: statMap.Speed,
    hitChance: statMap.HitChance,
    critChance: statMap.CritChance,
    critMulti: statMap.CritMultiplier ? statMap.CritMultiplier * 100 : undefined,
    bandit: buildEl?.getAttribute('bandit') ?? undefined,
    pantheonMajor: buildEl?.getAttribute('pantheonMajorGod') ?? undefined,
    pantheonMinor: buildEl?.getAttribute('pantheonMinorGod') ?? undefined,
  };

  // Config summary — list active inputs
  const configInputs: string[] = [];
  for (const inp of Array.from(doc.querySelectorAll('Config Input'))) {
    const name = inp.getAttribute('name') ?? '';
    const bool = inp.getAttribute('boolean');
    const num = inp.getAttribute('number');
    const str = inp.getAttribute('string');
    if (bool === 'true') configInputs.push(name.replace(/^condition/, '').replace(/_/g, ' '));
    else if (num && num !== '0') configInputs.push(`${name}: ${num}`);
    else if (str) configInputs.push(`${name}: ${str}`);
  }
  if (configInputs.length) stats.configSummary = configInputs.slice(0, 8).join(', ');

  return { className, ascendClassName, level, itemSets, equipped, jewels, gemGroups, stats };
}

/** Try to extract a PoB code from a pobb.in URL by fetching /raw. */
export async function fetchPoBFromUrl(input: string): Promise<string> {
  const trimmed = input.trim();
  const pobbMatch = trimmed.match(/^https?:\/\/pobb\.in\/([A-Za-z0-9-_]+)/);
  if (!pobbMatch) return trimmed;
  const id = pobbMatch[1];
  // Use server-side proxy to avoid CORS
  const proxyUrl = `/proxy/pobb/${id}/raw`;
  const resp = await fetch(proxyUrl);
  if (!resp.ok) throw new Error(`pobb.in fetch failed (${resp.status})`);
  return (await resp.text()).trim();
}

/** Upload raw PoB code to pobb.in via the local proxy.
 *  Returns the shortcode (e.g. "BlEy8WOpfMXB") or null on failure. */
export async function uploadToPobbIn(rawCode: string): Promise<string | null> {
  try {
    const resp = await fetch('/proxy/pobb/upload', {
      method: 'POST',
      headers: { 'Content-Type': 'text/plain' },
      body: rawCode,
    });
    if (!resp.ok) return null;
    const id = (await resp.text()).trim();
    // Validate shape — pobb.in shortcodes are 8-16 alphanumeric chars
    if (/^[A-Za-z0-9_-]{6,20}$/.test(id)) return id;
    return null;
  } catch { return null; }
}
